import { memo } from 'react'
import { TableContainer } from '@mui/material'
import { Order } from '../../proto/generated/order'
import { OrdersNavButtonsDetailed } from '../postionsWidget'
import OpenOrdersTable from './OpenOrdersTable'
import { EmptyState, GhostRows } from '../common/EmptyState'
import ListSkeleton from '../common/ListSkeleton'

export type OrdersTableScrollAreaProps = {
    orders: Order[]
    tabType: OrdersNavButtonsDetailed
    emptyText: string
    /** Show ghost rows above the text. Turn off for filter or search misses. */
    showEmptyPreview?: boolean
    /** False while the order history is still being fetched; shows a skeleton instead of the empty state. */
    loaded?: boolean
    hideOrderStatus?: boolean
    variant?: 'sidebar' | 'widget'
}

const OrdersTableScrollArea: React.FC<OrdersTableScrollAreaProps> = ({
    orders,
    tabType,
    emptyText,
    showEmptyPreview = true,
    loaded = true,
    hideOrderStatus = false,
    variant = 'sidebar',
}) => {
    if (!loaded) {
        return <ListSkeleton variant="orders" />
    }

    if (orders.length === 0) {
        return <EmptyState preview={showEmptyPreview ? <GhostRows variant="orders" /> : undefined} text={emptyText} />
    }

    if (variant === 'widget') {
        return (
            <TableContainer sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflowX: 'auto' }}>
                <OpenOrdersTable orders={orders} tabType={tabType} stickyHeader hideOrderStatus={hideOrderStatus} />
            </TableContainer>
        )
    }

    return (
        <TableContainer>
            <OpenOrdersTable orders={orders} tabType={tabType} hideOrderStatus={hideOrderStatus} />
        </TableContainer>
    )
}

export default memo(
    OrdersTableScrollArea,
    (prev, next) =>
        prev.orders === next.orders &&
        prev.tabType === next.tabType &&
        prev.emptyText === next.emptyText &&
        prev.showEmptyPreview === next.showEmptyPreview &&
        prev.loaded === next.loaded &&
        prev.hideOrderStatus === next.hideOrderStatus &&
        prev.variant === next.variant
)
